import React, { useState, useEffect } from "react";
import { Box, FormControlLabel, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import "./styles.scss";
import { useAppSelector, useAppDispatch } from "@hooks";
import { RootState } from "@Store";
import { selectCurrentOrganization, selectIsAdminOfCurrentOrganization } from "@Store/Organizations/selectors";
import { selectAppUserData } from "@Store/AppUser/selectors";
import { updateOrgAppSettingsRequest } from "@Store/Organizations/actions";

type OrgAppSettingsForm = {
  screenshotAllowed: boolean;
  callsDisabled: boolean;
  emailAddressHidden: boolean;
  mobileNumberHidden: boolean;
  favouritesDisabled: boolean;
};

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { currentOrganization } =
    useAppSelector((state: RootState) => ({
      currentOrganization: selectCurrentOrganization(state),
      userData: selectAppUserData(state),
      isAdminOfCurrentOrganization: selectIsAdminOfCurrentOrganization(state),
    }));

  const [disallowScreenshots, setDisallowScreenshots] = useState<boolean>(
    !(currentOrganization?.appSettings?.screenshotAllowed ?? true)
  );

  const [callsDisabled, setCallsDisabled] = useState<boolean>(
    currentOrganization?.appSettings?.callsDisabled ?? false
  );

  const [favouritesDisabled, setFavouritesDisabled] = useState<boolean>(
    currentOrganization?.appSettings?.favouritesDisabled ?? false
  );

  const [emailAddressHidden, setEmailAddressHidden] = useState<boolean>(
    currentOrganization?.appSettings?.emailAddressHidden ?? true
  );

  const [mobileNumberHidden, setMobileNumberHidden] = useState<boolean>(
    currentOrganization?.appSettings?.mobileNumberHidden ?? false
  );

  useEffect(() => {
    setDisallowScreenshots(!(currentOrganization?.appSettings?.screenshotAllowed ?? true));
    setCallsDisabled(currentOrganization?.appSettings?.callsDisabled ?? false);
    setFavouritesDisabled(currentOrganization?.appSettings?.favouritesDisabled ?? false);
    setEmailAddressHidden(currentOrganization?.appSettings?.emailAddressHidden ?? true);
    setMobileNumberHidden(currentOrganization?.appSettings?.mobileNumberHidden ?? false);
  }, [
    currentOrganization?.appSettings?.screenshotAllowed,
    currentOrganization?.appSettings?.callsDisabled,
    currentOrganization?.appSettings?.favouritesDisabled,
    currentOrganization?.appSettings?.emailAddressHidden,
    currentOrganization?.appSettings?.mobileNumberHidden,
  ]);

  const persistAppSettings = (next: OrgAppSettingsForm) => {
    if (!currentOrganization?.orgId) return;
    dispatch(updateOrgAppSettingsRequest({
      orgId: currentOrganization.orgId,
      appSettings: next,
    }));
  };

  const handleScreenshotToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setDisallowScreenshots(newValue);
    persistAppSettings({
      screenshotAllowed: !newValue,
      callsDisabled,
      emailAddressHidden,
      mobileNumberHidden,
      favouritesDisabled,
    });
  };

  const handleCallsDisabledToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setCallsDisabled(newValue);
    persistAppSettings({
      screenshotAllowed: !disallowScreenshots,
      callsDisabled: newValue,
      emailAddressHidden,
      mobileNumberHidden,
      favouritesDisabled,
    });
  };

  const handleFavouritesDisabledToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setFavouritesDisabled(newValue);
    persistAppSettings({
      screenshotAllowed: !disallowScreenshots,
      callsDisabled,
      emailAddressHidden,
      mobileNumberHidden,
      favouritesDisabled: newValue,
    });
  };

  const handleEmailVisibilityToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setEmailAddressHidden(newValue);
    persistAppSettings({
      screenshotAllowed: !disallowScreenshots,
      callsDisabled,
      emailAddressHidden: newValue,
      mobileNumberHidden,
      favouritesDisabled,
    });
  };

  const handleMobileVisibilityToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setMobileNumberHidden(newValue);
    persistAppSettings({
      screenshotAllowed: !disallowScreenshots,
      callsDisabled,
      emailAddressHidden,
      mobileNumberHidden: newValue,
      favouritesDisabled,
    });
  };

  return (
    <Box className="root">
      <div style={{ width: "95%" }}>
        <h2>{t("settings.title")}</h2>
        <div className="SettingsContainer">
          <div className="SettingsContainerLeftSide">
            <div className="SettingsContainerLeftSideContent">
              <div className="settingItem">
                <FormControlLabel
                  control={
                    <Switch
                      checked={disallowScreenshots}
                      onChange={handleScreenshotToggle}
                    />
                  }
                  label={t("settings.disallowScreenshots")}
                  labelPlacement="end"
                />
              </div>
              <div className="settingItem">
                <FormControlLabel
                  control={
                    <Switch
                      checked={callsDisabled}
                      onChange={handleCallsDisabledToggle}
                    />
                  }
                  label={t("settings.callsDisabled")}
                  labelPlacement="end"
                />
              </div>
              <div className="settingItem">
                <FormControlLabel
                  control={
                    <Switch
                      checked={favouritesDisabled}
                      onChange={handleFavouritesDisabledToggle}
                    />
                  }
                  label={t("settings.favouritesDisabled")}
                  labelPlacement="end"
                />
              </div>

              <Typography
                variant="subtitle1"
                className="settingsSectionHeading"
                sx={{ mt: 3, mb: 1, fontWeight: 600 }}
              >
                {t("settings.contactVisibilityTitle")}
              </Typography>
              <Typography
                variant="body2"
                className="settingsSectionHint"
                sx={{ mb: 2, color: "#9E9E9E" }}
              >
                {t("settings.contactVisibilityHint")}
              </Typography>

              <div className="settingItem">
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailAddressHidden}
                      onChange={handleEmailVisibilityToggle}
                    />
                  }
                  label={t("settings.hideEmailAddresses")}
                  labelPlacement="end"
                />
              </div>
              <div className="settingItem">
                <FormControlLabel
                  control={
                    <Switch
                      checked={mobileNumberHidden}
                      onChange={handleMobileVisibilityToggle}
                    />
                  }
                  label={t("settings.hideMobileNumbers")}
                  labelPlacement="end"
                />
              </div>
            </div>
          </div>
          <div className="SettingsContainerRightSide" style={{display: "none"}}>
            <div className="SettingsContainerRightSideContent">
              {/* Right container content */}
            </div>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default Settings;
